import {
  addYears,
  isAfter,
  isBefore,
  isValid,
  min,
  startOfDay,
  subMonths,
  subYears,
} from 'date-fns'
import { toZonedTime } from 'date-fns-tz'
import { TFunction } from 'i18next'

import { DEFAULT_TZ } from '@/constants/date'
import { formatDateIso } from '@/utils/date/format-date-iso'

import {
  IGLOO_LOGS_DEFAULT_MONTHS,
  IGLOO_LOGS_MAX_YEARS,
  IGLOO_LOGS_ROLES,
} from './igloo-logs.constants'
import {
  IglooLogsBounds,
  IglooLogsBuilding,
  IglooLogsDates,
  IglooLogsResolution,
  IglooLogsValidationError,
} from './igloo-logs.types'

export const getPropertyToday = (timeZone: string | undefined, now: Date): Date => {
  const zoned = toZonedTime(now, timeZone || DEFAULT_TZ)

  return startOfDay(isValid(zoned) ? zoned : toZonedTime(now, DEFAULT_TZ))
}

export const getIglooLogsBounds = ({
  endDate,
  startDate,
  today,
}: IglooLogsDates): IglooLogsBounds => ({
  end: {
    max: startDate ? min([today, addYears(startDate, IGLOO_LOGS_MAX_YEARS)]) : today,
    min: startDate ?? undefined,
  },
  start: {
    max: endDate ?? today,
    min: subYears(endDate ?? today, IGLOO_LOGS_MAX_YEARS),
  },
})

export const resolveIglooLogsRange = ({
  endDate,
  startDate,
  today,
}: IglooLogsDates): IglooLogsResolution => {
  const end = endDate ?? today
  const start = startDate ?? subMonths(end, IGLOO_LOGS_DEFAULT_MONTHS)

  if (isAfter(start, today) || isAfter(end, today)) return { error: 'futureDate' }
  if (isAfter(start, end)) return { error: 'invalidRange' }
  if (isBefore(start, subYears(end, IGLOO_LOGS_MAX_YEARS))) return { error: 'rangeTooLong' }

  return { range: { endDate: formatDateIso(end), startDate: formatDateIso(start) } }
}

export const canDownloadIglooLogs = (
  role: string | null | undefined,
  building: IglooLogsBuilding | null | undefined,
): boolean =>
  IGLOO_LOGS_ROLES.some((allowedRole) => allowedRole === role) &&
  building?.iglooWorkAvailable === true &&
  building?.iglooWorkStaffMasterKeyAvailable === true

export const getIglooLogsValidationMessage = (
  t: TFunction<['management-users']>,
  error: IglooLogsValidationError,
): string => {
  switch (error) {
    case 'futureDate':
      return t('management-users:iglooLogs.validation.futureDate')
    case 'invalidRange':
      return t('management-users:iglooLogs.validation.invalidRange')
    case 'rangeTooLong':
      return t('management-users:iglooLogs.validation.rangeTooLong')
  }
}

export const getIglooLogsErrorMessage = (
  t: TFunction<['management-users']>,
  code?: string,
  status?: number,
): string => {
  switch (code) {
    case 'too_many_rows':
      return t('management-users:iglooLogs.errors.tooManyRows')
    case 'range_too_long':
      return getIglooLogsValidationMessage(t, 'rangeTooLong')
    case 'invalid_range':
      return getIglooLogsValidationMessage(t, 'invalidRange')
    case 'future_end_date':
    case 'future_start_date':
      return getIglooLogsValidationMessage(t, 'futureDate')
    case 'bad_request':
      return t('management-users:iglooLogs.errors.badRequest')
    case 'forbidden_role':
    case 'flag_disabled':
    case 'building_not_assigned':
      return t('management-users:iglooLogs.errors.noAccess')
    case 'invalid_building_timezone':
      return t('management-users:iglooLogs.errors.invalidTimezone')
    default:
      return status === 403
        ? t('management-users:iglooLogs.errors.noAccess')
        : t('management-users:iglooLogs.errors.generic')
  }
}
